import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { 
  Key, Globe, Link, Trash2, ShieldAlert, MessageSquare, EyeOff, Shield, 
  ShoppingCart, Check, Info, Clock, AlertTriangle, Sparkles
} from "lucide-react";

const services = [
  {
    id: 1,
    category: "Credential Neutralization",
    action: "Triggers an automatic password reset or MFA challenge in the client's IAM system (Okta, Azure AD).",
    price: 15,
    icon: Key,
    color: "#8b5cf6"
  },
  {
    id: 2,
    category: "Surface Web Takedowns",
    action: "Abuse Reporting: Filing automated DMCA or 'Terms of Service' violation reports to hosters.",
    price: 10,
    icon: Globe,
    color: "#3b82f6"
  },
  {
    id: 3,
    category: "Domain / Phishing Takedown",
    action: "Registrar Suspension: Working with registrars (GoDaddy, Namecheap) to suspend malicious domains.",
    price: 25,
    icon: Link,
    color: "#ef4444"
  },
  {
    id: 4,
    category: "File Host Removal",
    action: "Content Deletion: Requesting the cloud hosting provider to purge stolen credentials/PII.",
    price: 20,
    icon: Trash2,
    color: "#ec4899"
  },
  {
    id: 5,
    category: "Infrastructure Blacklisting",
    action: "Threat Intelligence Feed: Pushing malicious URLs straight to client firewalls and EDR agents.",
    price: 25,
    icon: ShieldAlert,
    color: "#f59e0b"
  },
  {
    id: 6,
    category: "Telegram / Discord Cleanup",
    action: "Platform Reporting: Mass-reporting chat groups violating private info sharing policies.",
    price: 10,
    icon: MessageSquare,
    color: "#10b981"
  },
  {
    id: 7,
    category: "Search Engine De-indexing",
    action: "Right to be Forgotten: Requesting indexing bots to remove cached PII snippets from search results.",
    price: 20,
    icon: EyeOff,
    color: "#6366F1"
  },
  {
    id: 8,
    category: "Brand Protection",
    action: "Identity Verification: Flagging fake executive profile impersonations used for phishing/BEC attacks.",
    price: 15,
    icon: Shield,
    color: "#a78bfa"
  },
];

const fmt = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

const TakedownPage: React.FC = () => {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [priority, setPriority] = useState<"standard" | "express" | "immediate">("standard");

  const toggle = (idx: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const selectedArr = Array.from(selected);
  const subtotal = useMemo(
    () => selectedArr.reduce((sum, idx) => sum + services[idx].price, 0),
    [selectedArr]
  );

  const priorityLabel = priority === "standard" ? "Standard" : priority === "express" ? "Express (+20%)" : "Immediate (+50%)";
  const prioritySurcharge = priority === "standard" ? 0 : priority === "express" ? subtotal * 0.2 : subtotal * 0.5;
  const total = subtotal + prioritySurcharge;

  const slaTime = priority === "standard" ? "24 - 48 Hours Standard SLA" : priority === "express" ? "8 - 12 Hours Express SLA" : "2 - 4 Hours Immediate Critical SLA";

  const handleRequest = () => {
    if (selected.size === 0) {
      toast.error("Please select at least one mitigation service.");
      return;
    }
    toast.success(`Takedown request initialized successfully! ${selected.size} active operational threads. Priority: ${priority.toUpperCase()}.`);
    setSelected(new Set());
  };

  return (
    <div style={S.page}>
      {/* Background glow */}
      <div style={S.bgGlow} aria-hidden="true" />

      {/* Hero Section */}
      <div style={S.hero}>
        <div style={S.heroBadge}>
          <Sparkles size={12} />
          Active Incident Mitigation Center
        </div>
        <h1 style={S.heroTitle}>Mitigation &amp; Takedown Services</h1>
        <p style={S.heroSub}>
          Deploy structured, multi-channel countermeasures to quarantine, black-list, and purge exposed client assets across clean web, deep web, and encrypted chat channels.
        </p>
      </div>

      {/* Main Two Column Layout */}
      <div style={S.container}>
        
        {/* Left Column: Services Grid */}
        <div style={S.leftCol}>
          <div style={S.sectionHeader}>
            <span style={S.sectionTitle}>Available Remediation Procedures</span>
            <span style={S.sectionSub}>{services.length} actions configured</span>
          </div>

          <div style={S.grid}>
            {services.map((svc, idx) => {
              const isSelected = selected.has(idx);
              const IconComp = svc.icon;
              return (
                <div
                  key={svc.id}
                  onClick={() => toggle(idx)}
                  style={{
                    ...S.card,
                    ...(isSelected ? S.cardSelected : {})
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = "translateY(-3px)";
                    e.currentTarget.style.borderColor = isSelected ? "#6366F1" : "var(--color-border-hover)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = "none";
                    e.currentTarget.style.borderColor = isSelected ? "#6366F1" : "var(--card-border)";
                  }}
                >
                  {/* Card Header */}
                  <div style={S.cardHeader}>
                    <div style={{
                      ...S.iconContainer,
                      background: svc.color + "18",
                      color: svc.color
                    }}>
                      <IconComp size={18} />
                    </div>
                    
                    {/* Checkbox indicator */}
                    <div style={{
                      ...S.checkbox,
                      ...(isSelected ? S.checkboxChecked : {})
                    }}>
                      {isSelected && <Check size={10} style={{ color: "#fff" }} />}
                    </div>
                  </div>

                  {/* Card Content */}
                  <div style={S.cardContent}>
                    <h3 style={S.cardTitle}>{svc.category}</h3>
                    <p style={S.cardDesc}>{svc.action}</p>
                  </div>

                  {/* Card Footer: Pricing */}
                  <div style={S.cardFooter}>
                    <span style={S.priceLabel}>Remediation Cost</span>
                    <span style={S.priceVal}>{fmt.format(svc.price)}<span style={{ fontSize: "0.65rem", fontWeight: 400, opacity: 0.5 }}> / task</span></span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Column: Calculator Summary */}
        <div style={S.rightCol}>
          <div style={S.calculator}>
            
            {/* Header */}
            <div style={S.calcHeader}>
              <h2 style={S.calcTitle}>Mitigation Calculator</h2>
              <p style={S.calcSub}>Create immediate response request</p>
            </div>

            {/* Priority Selector */}
            <div style={S.calcField}>
              <label style={S.calcLabel}>Response Priority SLA</label>
              <div style={S.priorityGroup}>
                <button 
                  onClick={() => setPriority("standard")}
                  style={{ ...S.priorityBtn, ...(priority === "standard" ? S.priorityBtnActive : {}) }}
                >
                  Standard
                </button>
                <button 
                  onClick={() => setPriority("express")}
                  style={{ ...S.priorityBtn, ...(priority === "express" ? S.priorityBtnActive : {}) }}
                >
                  Express
                </button>
                <button 
                  onClick={() => setPriority("immediate")}
                  style={{ ...S.priorityBtn, ...(priority === "immediate" ? S.priorityBtnActive : {}) }}
                >
                  Immediate
                </button>
              </div>
            </div>

            {/* SLA Alert banner */}
            <div style={{
              ...S.slaBanner,
              color: priority === "immediate" ? "#f87171" : priority === "express" ? "#fb923c" : "#818cf8",
              background: priority === "immediate" ? "rgba(239,68,68,0.06)" : priority === "express" ? "rgba(249,115,22,0.06)" : "rgba(0,102,224,0.06)",
              borderColor: priority === "immediate" ? "rgba(239,68,68,0.15)" : priority === "express" ? "rgba(249,115,22,0.15)" : "rgba(0,102,224,0.15)"
            }}>
              <Clock size={13} style={{ flexShrink: 0 }} />
              <span style={{ fontSize: "0.72rem", fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>{slaTime}</span>
            </div>

            {/* Selected Items Summary List */}
            <div style={S.selectedSection}>
              <span style={S.calcLabel}>Selected Operations ({selected.size})</span>
              <div style={S.selectedList}>
                {selectedArr.length === 0 ? (
                  <div style={S.emptyState}>
                    <Info size={14} style={{ opacity: 0.4 }} />
                    <span>Select mitigation services from the grid on the left to start.</span>
                  </div>
                ) : (
                  selectedArr.map((idx) => {
                    const svc = services[idx];
                    return (
                      <div key={svc.id} style={S.selectedItem}>
                        <div style={S.selectedDot} />
                        <span style={S.selectedName}>{svc.category}</span>
                        <span style={S.selectedPrice}>{fmt.format(svc.price)}</span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div style={S.divider} />

            {/* Cost Breakdown */}
            <div style={S.breakdown}>
              <div style={S.breakdownRow}>
                <span style={{ opacity: 0.5 }}>Subtotal Operations</span>
                <span style={{ fontWeight: 600 }}>{fmt.format(subtotal)}</span>
              </div>
              <div style={S.breakdownRow}>
                <span style={{ opacity: 0.5 }}>Priority Surcharge ({priorityLabel})</span>
                <span style={{ fontWeight: 600 }}>{fmt.format(prioritySurcharge)}</span>
              </div>
              
              <div style={{ ...S.breakdownRow, marginTop: 10, borderTop: "1px dashed var(--color-border)", paddingTop: 10 }}>
                <span style={{ fontWeight: 700, color: "var(--color-text)" }}>Total Cost Estimate</span>
                <span style={{ fontSize: "1.25rem", fontWeight: 700, color: "#818cf8", fontFamily: "'JetBrains Mono', monospace" }}>{fmt.format(total)}</span>
              </div>
            </div>

            {/* Dispatch Action CTA */}
            <button 
              onClick={handleRequest}
              disabled={selected.size === 0}
              style={{
                ...S.submitBtn,
                ...(selected.size === 0 ? S.submitBtnDisabled : {})
              }}
            >
              <ShoppingCart size={15} />
              Dispatch Mitigation Request
            </button>
            
            <div style={S.guaranteeBanner}>
              <AlertTriangle size={12} style={{ color: "var(--color-text-subtle)" }} />
              <span>SLA timeline takes effect immediately upon admin verification.</span>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
};

/* ── Style Definitions ── */
const S: Record<string, React.CSSProperties> = {
  page: { padding: "20px 28px 40px", maxWidth: 1600, margin: "0 auto", position: "relative" },
  bgGlow: { position: "absolute", top: -100, right: "10%", width: 400, height: 400, background: "rgba(0,102,224,0.05)", borderRadius: "50%", filter: "blur(100px)", pointerEvents: "none" },
  
  hero: { marginBottom: 32 },
  heroBadge: { display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(0,102,224,0.1)", border: "1px solid rgba(0,102,224,0.2)", color: "#818cf8", borderRadius: 99, padding: "4px 12px", fontSize: "0.7rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.03em" },
  heroTitle: { fontSize: "1.8rem", fontWeight: 800, color: "var(--color-text)", marginTop: 12 },
  heroSub: { fontSize: "0.85rem", color: "var(--color-text-muted)", marginTop: 6, maxWidth: 640, lineHeight: 1.4 },

  container: { display: "flex", gap: 32, flexWrap: "wrap" },
  leftCol: { flex: "3 1 600px", minWidth: 350 },
  rightCol: { flex: "1 1 350px", minWidth: 320, position: "sticky", top: 20, height: "fit-content" },

  sectionHeader: { display: "flex", flexDirection: "column", marginBottom: 16 },
  sectionTitle: { fontSize: "0.95rem", fontWeight: 700, color: "var(--color-text)" },
  sectionSub: { fontSize: "0.75rem", color: "var(--color-text-muted)", marginTop: 2 },

  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 },
  
  card: { background: "var(--card-bg)", border: "1px solid var(--card-border)", borderRadius: 12, padding: 20, cursor: "pointer", display: "flex", flexDirection: "column", gap: 14, transition: "all 0.2s ease", userSelect: "none" },
  cardSelected: { borderColor: "#6366F1", background: "rgba(0,102,224,0.04)" },
  cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  iconContainer: { width: 34, height: 34, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" },
  
  checkbox: { width: 18, height: 18, borderRadius: 5, border: "1px solid var(--color-border)", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s ease" },
  checkboxChecked: { border: "1px solid #6366F1", background: "#6366F1" },
  
  cardContent: { display: "flex", flexDirection: "column", gap: 6, flexGrow: 1 },
  cardTitle: { fontSize: "0.85rem", fontWeight: 700, color: "var(--color-text)" },
  cardDesc: { fontSize: "0.72rem", color: "var(--color-text-muted)", lineHeight: 1.35 },

  cardFooter: { borderTop: "1px solid var(--color-border)", paddingTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center" },
  priceLabel: { fontSize: "0.65rem", color: "var(--color-text-muted)" },
  priceVal: { fontSize: "0.85rem", fontWeight: 700, color: "var(--color-text)" },

  /* Calculator styles */
  calculator: { background: "var(--card-bg)", border: "1px solid var(--card-border)", borderRadius: 16, padding: 24, display: "flex", flexDirection: "column", gap: 20 },
  calcHeader: { display: "flex", flexDirection: "column", gap: 4 },
  calcTitle: { fontSize: "1rem", fontWeight: 700, color: "var(--color-text)" },
  calcSub: { fontSize: "0.72rem", color: "var(--color-text-muted)" },

  calcField: { display: "flex", flexDirection: "column", gap: 8 },
  calcLabel: { fontSize: "0.7rem", fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" },
  
  priorityGroup: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, background: "var(--color-surface-2)", padding: 4, borderRadius: 8, border: "1px solid var(--color-border)" },
  priorityBtn: { background: "none", border: "none", outline: "none", color: "var(--color-text-muted)", padding: "6px 0", fontSize: "0.7rem", fontWeight: 600, borderRadius: 6, cursor: "pointer", transition: "all 0.15s ease" },
  priorityBtnActive: { background: "rgba(0,102,224,0.15)", color: "#818cf8", border: "1px solid rgba(0,102,224,0.2)" },

  slaBanner: { display: "flex", alignItems: "center", gap: 8, border: "1px solid transparent", borderRadius: 8, padding: "8px 12px" },
  
  selectedSection: { display: "flex", flexDirection: "column", gap: 8 },
  selectedList: { display: "flex", flexDirection: "column", gap: 6, maxHeight: 180, overflowY: "auto", paddingRight: 4 },
  selectedItem: { display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "var(--color-surface-2)", border: "1px solid var(--color-border)", borderRadius: 8, fontSize: "0.72rem" },
  selectedDot: { width: 5, height: 5, borderRadius: 99, background: "#818cf8" },
  selectedName: { color: "var(--color-text)", flexGrow: 1, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" },
  selectedPrice: { fontWeight: 600, color: "var(--color-text)", fontFamily: "'JetBrains Mono', monospace" },
  
  emptyState: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", gap: 8, padding: "24px 12px", color: "var(--color-text-muted)", fontSize: "0.7rem" },

  divider: { height: 1, background: "var(--color-border)" },
  
  breakdown: { display: "flex", flexDirection: "column", gap: 8, fontSize: "0.72rem", color: "var(--color-text-muted)" },
  breakdownRow: { display: "flex", justifyContent: "space-between", alignItems: "center" },

  submitBtn: { display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: "#6366F1", border: "none", outline: "none", color: "#fff", padding: "12px", borderRadius: 8, fontSize: "0.75rem", fontWeight: 700, cursor: "pointer", transition: "all 0.15s ease", boxShadow: "0 4px 15px rgba(0,102,224,0.25)" },
  submitBtnDisabled: { background: "var(--color-surface-2)", color: "var(--color-text-muted)", cursor: "not-allowed", border: "1px dashed var(--color-border)", boxShadow: "none" },

  guaranteeBanner: { display: "flex", alignItems: "center", gap: 6, fontSize: "0.62rem", color: "var(--color-text-subtle)", justifyContent: "center", marginTop: 4 }
};

export default TakedownPage;
